import { useState } from "react";
import { ChevronLeft, ChevronRight, Download, MoreVertical, PlusSquare, Share } from "lucide-react";
import { isAndroid } from "@/lib/push";

const ICON = "/icon-192.png?v=6";
const Pulse = ({ children, className = "" }) => <span className={`relative inline-flex items-center justify-center ${className}`}><span className="absolute inset-0 rounded-full bg-rose-400/50 animate-ping" />{children}</span>;

const Phone = ({ children }) => (
  <div className="mx-auto w-[150px] h-[230px] rounded-[1.6rem] bg-slate-900 p-1.5 shadow-xl shadow-rose-200/70">
    <div className="relative w-full h-full rounded-[1.25rem] overflow-hidden bg-gradient-to-b from-rose-50 to-pink-100">{children}</div>
  </div>
);

const FakeSite = () => (
  <div className="absolute inset-x-2 top-6 space-y-1.5">
    <div className="h-3 w-16 rounded-full pink-gradient" />
    {[0, 1, 2].map((i) => <div key={i} className="h-10 rounded-lg bg-white/90" />)}
  </div>
);

const Row = ({ icon: Icon, label, hot }) => (
  <div className={`flex items-center justify-between px-2 py-1.5 text-[9px] font-semibold ${hot ? "bg-rose-50 text-rose-600 ring-2 ring-rose-400 rounded-md" : "text-slate-500"}`}>
    {label}{Icon && <Icon className="w-3 h-3" />}
  </div>
);

const HomeScreen = () => (
  <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-300 via-rose-300 to-amber-200 p-3 grid grid-cols-3 gap-2 content-start">
    {Array.from({ length: 8 }).map((_, i) => <span key={i} className="aspect-square rounded-lg bg-white/40" />)}
    <span className="relative aspect-square"><span className="absolute -inset-1 rounded-xl bg-white/70 animate-ping" /><img src={ICON} alt="" className="relative w-full h-full rounded-lg shadow-lg" /></span>
  </div>
);

const IOS = [
  ["Tippe unten in Safari auf „Teilen“", <><FakeSite /><div className="absolute inset-x-0 bottom-0 h-8 bg-white/95 flex items-center justify-around text-slate-400"><ChevronLeft className="w-3 h-3" /><Pulse className="w-6 h-6 text-sky-500"><Share className="relative w-4 h-4" /></Pulse><MoreVertical className="w-3 h-3 rotate-90" /></div></>],
  ["Scrolle und wähle „Zum Home-Bildschirm“", <><FakeSite /><div className="absolute inset-x-1.5 bottom-1.5 rounded-xl bg-white shadow-lg py-1 divide-y divide-slate-100"><Row label="Kopieren" /><Row label="Zur Leseliste" /><Row icon={PlusSquare} label="Zum Home-Bildschirm" hot /><Row label="Lesezeichen" /></div></>],
  ["Tippe oben rechts auf „Hinzufügen“ – fertig!", <HomeScreen />],
];
const ANDROID = [
  ["Tippe oben rechts in Chrome auf ⋮", <><div className="absolute inset-x-0 top-0 h-7 bg-white/95 flex items-center justify-between px-2"><span className="h-3 w-20 rounded-full bg-slate-100" /><Pulse className="w-5 h-5 text-slate-700"><MoreVertical className="relative w-3.5 h-3.5" /></Pulse></div><FakeSite /></>],
  ["Wähle „App installieren“ bzw. „Zum Startbildschirm“", <><FakeSite /><div className="absolute right-1.5 top-6 w-28 rounded-lg bg-white shadow-lg py-1"><Row label="Neuer Tab" /><Row label="Verlauf" /><Row icon={Download} label="App installieren" hot /><Row label="Teilen" /></div></>],
  ["Bestätige mit „Installieren“ – fertig!", <HomeScreen />],
];

export function InstallSteps({ compact }) {
  const [os, setOs] = useState(isAndroid() ? "android" : "ios");
  const [i, setI] = useState(0);
  const steps = os === "ios" ? IOS : ANDROID;
  const [title, scene] = steps[i];
  return (
    <div className="space-y-3" data-testid="install-steps">
      <div className="flex justify-center gap-2">
        {[["ios", "iPhone"], ["android", "Android"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => { setOs(k); setI(0); }} data-testid={`install-steps-tab-${k}`}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${os === k ? "bg-rose-500 text-white" : "bg-rose-50 text-rose-600"}`}>{l}</button>
        ))}
      </div>
      <div key={os + i} className="fade-up space-y-3">
        <Phone>{scene}</Phone>
        <p className={`font-bold text-slate-800 text-center ${compact ? "text-sm" : "text-base"}`} data-testid="install-steps-title"><span className="text-rose-500">Schritt {i + 1}:</span> {title}</p>
      </div>
      <div className="flex items-center justify-center gap-3">
        <button type="button" onClick={() => setI((n) => Math.max(0, n - 1))} disabled={i === 0} aria-label="Zurück" data-testid="install-steps-prev" className="w-9 h-9 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
        <div className="flex gap-1.5">{steps.map((_, n) => <span key={n} className={`h-2 rounded-full transition-[width,background-color] ${n === i ? "w-6 bg-rose-500" : "w-2 bg-rose-200"}`} />)}</div>
        <button type="button" onClick={() => setI((n) => Math.min(steps.length - 1, n + 1))} disabled={i === steps.length - 1} aria-label="Weiter" data-testid="install-steps-next" className="w-9 h-9 rounded-full pink-gradient text-white flex items-center justify-center disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
